import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Copy, Check, TicketPercent } from "lucide-react";

const CODE = "edu25";
const CATALOG_URL = "https://edunation.com.pl/szkolenia-i-kursy/";

export const ThankYou = () => {
    const [copied, setCopied] = useState(false);
    const purchaseFired = useRef(false);

    // Meta Pixel: konwersja Purchase. Wartość i walutę można przekazać w URL
    // (np. /dziekuje?value=299&currency=PLN) - jeśli ich brak, wysyłamy samo zdarzenie.
    useEffect(() => {
        if (purchaseFired.current) return;
        purchaseFired.current = true;
        const params = new URLSearchParams(window.location.search);
        const value = parseFloat(params.get("value"));
        const payload = {
            content_name: "100 rozwiązań na trudne sytuacje na lekcji z dorosłymi",
            currency: params.get("currency") || "PLN",
        };
        if (!Number.isNaN(value)) payload.value = value;
        window.fbq?.("track", "Purchase", payload);
    }, []);

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(CODE);
        } catch {
            const input = document.createElement("input");
            input.value = CODE;
            document.body.appendChild(input);
            input.select();
            document.execCommand("copy");
            document.body.removeChild(input);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
    };

    return (
        <main
            data-testid="thankyou-page"
            className="relative min-h-screen bg-cream overflow-hidden flex flex-col"
        >
            <div className="absolute -top-10 -left-10 w-44 h-44 bg-hl-cyan rounded-[3rem] rotate-12" />
            <div className="absolute top-1/3 -right-14 w-56 h-56 bg-hl-pink rounded-full" />
            <div className="absolute bottom-10 left-1/4 w-32 h-32 bg-hl-lav rounded-[2rem] -rotate-6" />

            <header className="relative z-10 py-8 flex justify-center">
                <a href="/" aria-label="EduNation - wróć na stronę główną">
                    <img src="/images/logo.svg" alt="EduNation" className="h-8 w-auto" />
                </a>
            </header>

            <div className="relative z-10 flex-1 flex items-center">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 grid lg:grid-cols-12 gap-12 lg:gap-10 items-center w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-5 relative order-2 lg:order-1"
                    >
                        <div className="rounded-[2.5rem] overflow-hidden shadow-card bg-white border border-border">
                            <img
                                src="/images/trenerki.webp"
                                alt="Anna Popławska i Luiza Wójtowicz-Waga - zespół EduNation"
                                className="w-full h-[380px] sm:h-[480px] object-cover object-top"
                            />
                        </div>
                        <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-ink text-white text-xs font-bold uppercase tracking-[0.14em] px-5 py-2.5 shadow-lg">
                            Zespół EduNation
                        </div>
                    </motion.div>

                    <div className="lg:col-span-7 order-1 lg:order-2 text-center lg:text-left">
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            data-testid="thankyou-heading"
                            className="font-display uppercase text-3xl sm:text-5xl lg:text-[3.4rem] font-extrabold tracking-tight leading-[1.08] text-ink"
                        >
                            <span className="inline-block bg-tang px-3 sm:px-5 rounded-xl -rotate-1">
                                Dziękujemy
                            </span>{" "}
                            za zaufanie i za zakup na naszej Platformie edunation!
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="mt-7 text-base md:text-lg text-ink/75 font-medium leading-relaxed max-w-xl mx-auto lg:mx-0"
                        >
                            Dziękując Ci za to, że z nami jesteś i aby umilić kolejne zakupy,
                            przygotowałyśmy dla Ciebie{" "}
                            <strong className="text-ink">kod zniżkowy na 25%!</strong>
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.7, delay: 0.45 }}
                            className="mt-8 inline-flex flex-col sm:flex-row items-center gap-4 sm:gap-6 rounded-3xl bg-white border-2 border-ink px-7 py-6 shadow-pop"
                        >
                            <div className="flex items-center gap-3">
                                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-hl-pink text-ink shrink-0">
                                    <TicketPercent className="w-6 h-6" />
                                </span>
                                <span className="text-left">
                                    <span className="block text-xs uppercase tracking-[0.18em] font-bold text-ink-mute">
                                        Twój kod na kolejne zakupy to:
                                    </span>
                                    <span
                                        data-testid="thankyou-code-value"
                                        className="block font-display font-extrabold text-4xl text-ink tracking-wide"
                                    >
                                        {CODE}
                                    </span>
                                </span>
                            </div>
                            <button
                                data-testid="thankyou-copy-button"
                                onClick={copyCode}
                                className={`inline-flex items-center gap-2 rounded-full font-bold px-5 min-h-[44px] text-sm transition-all duration-300 ${
                                    copied
                                        ? "bg-hl-cyan text-ink"
                                        : "bg-ink text-white hover:bg-ink-soft"
                                }`}
                            >
                                {copied ? (
                                    <>
                                        <Check className="w-4 h-4" />
                                        Skopiowano!
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-4 h-4" />
                                        Kopiuj kod
                                    </>
                                )}
                            </button>
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="mt-4 text-sm text-ink/55 font-medium"
                        >
                            *kod zakupowy jest ważny przez 7 dni.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.7 }}
                            className="mt-8"
                        >
                            <a
                                data-testid="thankyou-catalog-button"
                                href={CATALOG_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 rounded-full bg-tang hover:bg-[#EFE238] text-ink font-bold uppercase tracking-wide px-8 min-h-[56px] text-sm transition-all duration-300 hover:-translate-y-0.5 shadow-md"
                            >
                                Przejdź do katalogu szkoleń
                                <ArrowRight className="w-5 h-5" />
                            </a>
                        </motion.div>
                    </div>
                </div>
            </div>
        </main>
    );
};
